import React from "react";
import Modal from "../../components/Modal.jsx";
import { minutesToTimeLabel, formatDateLabel } from "../../classroomSchedule.js";
import "../../../../components/common/waypoint/waypoint.css";

/* Who and what is behind one of the dashboard's six figures.

   The figures are counts -- "Labs occupied 7" -- and Waypoint addresses
   one record by id, so a tile cannot be a Waypoint itself: there is no
   "occupied labs" record to open. What a count prompts is "which ones?",
   so the tile opens this list, and every record IN the list is a
   Waypoint: a trainer opens the trainer, a batch opens the batch. Same
   shape as the Teaching Summary chips on the trainer dashboard.

   Labs are the exception. Waypoint's classroom type is details only,
   and the page's own room popup is the fuller view here (the day's
   sessions, free slots, the out-of-order toggle), so a lab row opens
   that.

   The list is the view popup's table (view_popup_rules): .pgm-table in a
   .pgm-tablescroll, identity first as a name over its detail, and the
   ROW is the control. It was an lp-dialog-table with a "Sr. No" column,
   a bare lab number, batches and trainers split into two columns that
   no longer lined up with each other, and a "Details" link at the end
   of every row -- an action column, which a view list does not have. */

const trainerName = (t) => t?.trainerName || t?.name || t?.trainerEmail || "Unassigned";

// One entry per batch, however many dates in the applied range it meets
// on -- a week's range would otherwise list it five times.
const uniqueBy = (list, key) => {
  const seen = new Map();
  (list || []).forEach((item) => {
    const k = item?.[key];
    if (k && !seen.has(k)) seen.set(k, item);
  });
  return [...seen.values()];
};

// 12-hour, the same as the day popups, so a time reads the same wherever
// on this page it appears.
const timeOf = (s) => `${minutesToTimeLabel(s.startMin)} – ${minutesToTimeLabel(s.endMin)}`;

/* The whole row opens its record: click, Enter or Space. A link inside
   the row (a batch, a trainer) is its own control -- its click must not
   also open the row, and an Enter pressed on it must not either. */
const rowProps = (label, open) =>
  open
    ? {
        className: "pgm-row--open",
        tabIndex: 0,
        role: "button",
        "aria-label": label,
        onClick: open,
        onKeyDown: (e) => {
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            open();
          }
        },
      }
    : {};

const inner = (fn) => (e) => {
  e.stopPropagation();
  fn();
};

/* What is running: one line per batch, the batch as a link over its time
   and trainer. Batch and trainer used to be two separate columns, so with
   two batches in a lab nothing said which trainer belonged to which. */
const Sessions = ({ sessions, withRoom, onOpenEntity }) => {
  const batches = uniqueBy(sessions, "batchId");
  if (!batches.length) return <span className="pgm-empty">—</span>;
  return (
    <div className="lp-cell">
      {batches.map((s) => (
        <div key={s.batchId} className="pgm-person-id">
          <button
            type="button"
            className="wp-link pgm-person-name"
            onClick={inner(() => onOpenEntity("batch", s.batchId))}
          >
            {s.batchName || "Unnamed batch"}
          </button>
          <span className="pgm-person-mail">
            {timeOf(s)}
            {withRoom && s.classroomNo ? ` · ${s.classroomNo}` : ""}
            {!withRoom && (
              <>
                {" · "}
                {s.trainerId ? (
                  <button
                    type="button"
                    className="wp-link"
                    onClick={inner(() => onOpenEntity("trainer", s.trainerId))}
                  >
                    {s.trainerName || "Unassigned"}
                  </button>
                ) : (
                  "No trainer"
                )}
              </>
            )}
          </span>
        </div>
      ))}
    </div>
  );
};

const RoomTable = ({ rows, status, onOpenEntity, onOpenRoom }) => (
  <div className="pgm-tablescroll">
    <table className="pgm-table pgm-table--values">
      <thead>
        <tr>
          <th className="pgm-num">#</th>
          <th>Lab</th>
          {status === "occupied" && <th>Running</th>}
          {status === "maintenance" && <th>Reason</th>}
          {status === "free" && <th className="pgm-num">Seats</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((room, i) => (
          <tr
            key={room.classroom.id || i}
            {...rowProps(`Open ${room.classroom.classroomNo || "this lab"}`, () => onOpenRoom(room))}
          >
            <td className="pgm-num">{i + 1}</td>
            <td>
              <span className="pgm-person-id">
                <span className="pgm-person-name">{room.classroom.classroomNo}</span>
                <span className="pgm-person-mail">
                  {room.classroom.branchName || "No branch"}
                  {room.classroom.floor ? ` · ${room.classroom.floor}` : ""}
                </span>
              </span>
            </td>
            {status === "occupied" && (
              <td>
                <Sessions sessions={room.rangeSessions} onOpenEntity={onOpenEntity} />
              </td>
            )}
            {status === "maintenance" && (
              <td>{room.classroom.reason || <span className="pgm-empty">No reason given</span>}</td>
            )}
            {status === "free" && <td className="pgm-num">{room.classroom.capacity ?? "—"}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TrainerTable = ({ rows, status, onOpenEntity }) => (
  <div className="pgm-tablescroll">
    <table className="pgm-table pgm-table--values">
      <thead>
        <tr>
          <th className="pgm-num">#</th>
          <th>Trainer</th>
          {status === "teaching" && <th>Teaching</th>}
          {status === "absent" && <th>Leave</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={row.trainer.id || i}
            {...rowProps(
              `Open ${trainerName(row.trainer)}`,
              row.trainer.id ? () => onOpenEntity("trainer", row.trainer.id) : null,
            )}
          >
            <td className="pgm-num">{i + 1}</td>
            <td>
              <span className="pgm-person-id">
                <span className="pgm-person-name">{trainerName(row.trainer)}</span>
                <span className="pgm-person-mail">
                  {row.trainer.specialization || "No specialization on file"}
                </span>
              </span>
            </td>
            {status === "teaching" && (
              <td>
                <Sessions sessions={row.rangeSessions} withRoom onOpenEntity={onOpenEntity} />
              </td>
            )}
            {status === "absent" && (
              <td>
                <span className="pgm-person-id">
                  <span className="pgm-person-name">{row.leave?.reason || "On leave"}</span>
                  {row.leave?.note && <span className="pgm-person-mail">{row.leave.note}</span>}
                </span>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* The "Lab overlaps" figure: one row per pair of running batches saved
   into one lab at the same time -- from before the batch save refused
   them. The lab over its branch, both batches (each a Waypoint, where it
   can be moved), and where they meet: the shared time, weekdays and the
   dates still to come. */
const shortDay = (d) => d.charAt(0).toUpperCase() + d.slice(1, 3);

const OverlapTable = ({ rows, onOpenEntity }) => (
  <div className="pgm-tablescroll">
    <table className="pgm-table pgm-table--values">
      <thead>
        <tr>
          <th className="pgm-num">#</th>
          <th>Lab</th>
          <th>Batches</th>
          <th>Both booked</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o, i) => (
          <tr key={o.key}>
            <td className="pgm-num">{i + 1}</td>
            <td>
              <span className="pgm-person-id">
                <span className="pgm-person-name">{o.classroom.classroomNo}</span>
                <span className="pgm-person-mail">
                  {o.classroom.branchName || "No branch"}
                  {o.classroom.floor ? ` · ${o.classroom.floor}` : ""}
                </span>
              </span>
            </td>
            <td>
              <Sessions sessions={o.batches} onOpenEntity={onOpenEntity} />
            </td>
            <td>
              <span className="pgm-person-id">
                <span className="pgm-person-name">{timeOf(o)}</span>
                <span className="pgm-person-mail">
                  {o.days.map(shortDay).join(", ")} · {formatDateLabel(o.fromDate)} – {formatDateLabel(o.toDate)}
                </span>
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StatBreakdownModal = ({ breakdown, onClose, onOpenEntity, onOpenRoom }) => {
  const { kind, status, label, rows, loading, error } = breakdown;
  const isRoom = kind === "room";
  const isOverlap = kind === "overlap";
  const count = rows.length;
  const noun = isOverlap ? "overlap" : isRoom ? "lab" : "trainer";

  let body;
  if (loading) {
    body = (
      <div className="pg-state">
        <span className="pg-spinner" role="status" aria-hidden="true" />
        <p className="pg-state-text">Loading the list...</p>
      </div>
    );
  } else if (error) {
    body = <p className="pgm-empty">{error}</p>;
  } else if (!count) {
    body = <p className="pgm-empty">Nothing to list for the range applied.</p>;
  } else if (isOverlap) {
    body = <OverlapTable rows={rows} onOpenEntity={onOpenEntity} />;
  } else if (isRoom) {
    body = <RoomTable rows={rows} status={status} onOpenEntity={onOpenEntity} onOpenRoom={onOpenRoom} />;
  } else {
    body = <TrainerTable rows={rows} status={status} onOpenEntity={onOpenEntity} />;
  }

  return (
    <Modal
      eyebrow={isRoom || isOverlap ? "Labs" : "Trainers"}
      title={label}
      sub={loading ? undefined : `${count} ${noun}${count === 1 ? "" : "s"}`}
      onClose={onClose}
      size="wide"
      labelledBy="cls-breakdown-title"
      footer={
        <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
          Close
        </button>
      }
    >
      {body}
    </Modal>
  );
};

export default StatBreakdownModal;
